// ============================================================
// The image viewport (v8.364.0) - what the lightbox shows of a picture.
//
// A view is a scale and where the image's top-left sits in the box
// (CSS `translate(x, y) scale(s)`, origin top-left). The rules: the floor
// is the smaller of "fit" (the whole picture, upscaled if it is small) and
// 1:1 - so a small picture can be seen at its true pixels and a large one
// never smaller than the box; never past MAX_ZOOM; a picture smaller than
// the box stays centred, a larger one may not leave a gap. Pure; the
// component owns the events.
// ============================================================
export interface Size {
  width: number;
  height: number;
}
export interface Point {
  x: number;
  y: number;
}
export interface View {
  scale: number;
  x: number;
  y: number;
}

export const MAX_ZOOM = 8;
const STEP = 1.25;

/** The scale that shows the whole image as large as the box allows. 1 when either is empty. */
export function fitScale(img: Size, box: Size): number {
  if (img.width <= 0 || img.height <= 0 || box.width <= 0 || box.height <= 0) return 1;
  return Math.min(box.width / img.width, box.height / img.height);
}

/** The image at `scale`, in the middle of the box. */
export function centred(img: Size, box: Size, scale: number): View {
  return { scale, x: (box.width - img.width * scale) / 2, y: (box.height - img.height * scale) / 2 };
}

/** Each axis: centred when the image is narrower than the box, else no gap on either side. */
function clampAxis(pos: number, imgLen: number, boxLen: number): number {
  if (imgLen <= boxLen) return (boxLen - imgLen) / 2;
  return Math.min(0, Math.max(boxLen - imgLen, pos));
}

/** The smallest scale allowed: fit, or 1:1 when the picture is smaller than the box. */
export function minScale(fit: number): number {
  return Math.min(fit, 1);
}

export function clampView(v: View, img: Size, box: Size, fit: number): View {
  const scale = Math.min(MAX_ZOOM, Math.max(minScale(fit), v.scale));
  return {
    scale,
    x: clampAxis(v.x, img.width * scale, box.width),
    y: clampAxis(v.y, img.height * scale, box.height),
  };
}

/** Multiply the scale by `factor`, keeping the image point under `cursor` (box coordinates) still. */
export function zoomAt(v: View, factor: number, cursor: Point, img: Size, box: Size, fit: number): View {
  const scale = Math.min(MAX_ZOOM, Math.max(minScale(fit), v.scale * factor));
  const k = scale / v.scale;
  return clampView({ scale, x: cursor.x - (cursor.x - v.x) * k, y: cursor.y - (cursor.y - v.y) * k }, img, box, fit);
}

/** One notch in or out, about the box's centre - the buttons and the keys. */
export function zoomStep(v: View, direction: 1 | -1, img: Size, box: Size, fit: number): View {
  return zoomAt(v, direction > 0 ? STEP : 1 / STEP, { x: box.width / 2, y: box.height / 2 }, img, box, fit);
}

export function isFit(v: View, fit: number): boolean {
  return Math.abs(v.scale - fit) < 1e-6;
}

/** The click: fit → 1:1 at the cursor; anything else → fit. */
export function toggleFit(v: View, cursor: Point, img: Size, box: Size, fit: number): View {
  return isFit(v, fit) ? zoomAt(v, 1 / v.scale, cursor, img, box, fit) : centred(img, box, fit);
}
